import React, { useState } from 'react';
import { Globe, Zap } from 'lucide-react';

/* ============================================================
   TERRAFORM DEVICE — Signature system page
   (Extracted from Features so it can live on its own /terraform route)
   ============================================================ */

const INK = 'var(--text-primary)';
const STEEL = 'var(--text-secondary)';
const RED = '#FF8618';

const TERRAFORM_PHASES = [
  {
    id: 'cycle',
    label: 'Cycle Activation',
    phase: '01 — Phase',
    icon: Zap,
    heading: 'Cycle Activation',
    body: '10 Terraform Devices are scattered across planet E-47. Hunt Key Cards through timed cycles, hold contested zones, and activate a device to lock the next phase.',
    devices: '10 → 3',
    cycles: '03',
  },
  {
    id: 'atmosphere',
    label: 'Atmosphere Lock',
    phase: '02 — Phase',
    icon: Globe,
    heading: 'Atmosphere Lock',
    body: "Once activated, the device reshapes the local atmosphere to match your race's home planet. Your team gains powerful buffs while opponents struggle in hostile conditions.",
    devices: '03 → 1',
    cycles: '01',
  },
];

const TerraformDevice = () => {
  const [activePhase, setActivePhase] = useState('cycle');
  const active = TERRAFORM_PHASES.find((t) => t.id === activePhase) || TERRAFORM_PHASES[0];

  return (
    <section
      id="terraform"
      data-testid="terraform-section"
      className="relative pt-14 pb-16 px-6 overflow-hidden"
    >
      {/* Top-right angular tech lines */}
      <div className="absolute top-0 right-0 w-[60%] h-[420px] pointer-events-none" style={{ opacity: 0.5 }}>
        <svg viewBox="0 0 800 400" className="w-full h-full" preserveAspectRatio="none">
          <path d="M800 60 L520 60 L470 110 L800 110" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="1.5" />
          <path d="M800 130 L560 130 L520 170 L800 170" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="1.5" />
          <path d="M800 200 L620 200 L590 230 L800 230" fill="none" stroke="rgba(255,134,24,0.12)" strokeWidth="1.5" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto relative z-10 w-full">
        {/* ===== TERRAFORM DEVICE ===== */}
        <div data-testid="terraform-block">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs uppercase tracking-[0.45em]" style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 700, color: RED }}>Signature System</span>
              <div className="flex items-center gap-1">
                <span className="block h-[2px] w-6" style={{ background: RED }} />
                <span className="block h-[2px] w-3" style={{ background: RED }} />
                <span className="block h-[2px] w-2" style={{ background: 'rgba(255,134,24,0.5)' }} />
              </div>
            </div>
            <h2 className="font-black uppercase leading-[0.9]" style={{ fontFamily: '"Russo One", "Black Ops One", sans-serif', fontWeight: 900, fontSize: 'clamp(2.6rem, 6.4vw, 5rem)', letterSpacing: '0.01em' }} data-testid="terraform-title">
              <span style={{ color: 'var(--text-primary)', textShadow: '0 1px 0 #fff, 0 2px 1px rgba(120,135,150,0.55), 0 6px 16px rgba(40,55,70,0.18)' }}>TERRAFORM</span>
              <br />
              <span style={{ color: RED, textShadow: '0 2px 14px rgba(255,134,24,0.30), 0 1px 0 rgba(255,255,255,0.4)' }}>DEVICE</span>
            </h2>
            <p className="mt-4 max-w-2xl text-base" style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 500, color: STEEL }}>
              The cornerstone of Cosmic Conquest — environmental warfare, weaponized.
            </p>
          </div>

          <div className="relative grid lg:grid-cols-12 gap-6 items-center">
            {/* LEFT — frosted phase panel */}
            <div className="lg:col-span-5 relative z-10" data-testid="terraform-text-card">
              <div
                className="relative p-7 md:p-9 rounded-2xl"
                style={{
                  background: 'rgba(16,22,28,0.6)',
                  backdropFilter: 'blur(16px) saturate(130%)',
                  WebkitBackdropFilter: 'blur(16px) saturate(130%)',
                  border: 'none',
                  boxShadow: '0 24px 60px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)',
                }}
              >
                {/* borders removed */}
                <span className="block text-[11px] uppercase tracking-[0.4em] mb-3" style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 700, color: RED }}>{active.phase}</span>
                <h3 className="text-2xl md:text-3xl font-black uppercase tracking-[0.02em] leading-tight mb-4" style={{ fontFamily: '"Russo One", sans-serif', color: INK }}>{active.heading}</h3>
                <p className="text-sm leading-relaxed mb-6" style={{ fontFamily: '"Rajdhani", sans-serif', fontWeight: 500, color: STEEL }}>{active.body}</p>
                <div className="h-px mb-5" style={{ background: 'rgba(255,134,24,0.3)' }} />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] font-bold tracking-[0.3em] uppercase mb-1" style={{ fontFamily: '"Rajdhani", sans-serif', color: 'var(--text-muted)' }}>Devices</p>
                    <p className="text-2xl font-black" style={{ fontFamily: '"Russo One", sans-serif', color: INK }}>{active.devices}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold tracking-[0.3em] uppercase mb-1" style={{ fontFamily: '"Rajdhani", sans-serif', color: 'var(--text-muted)' }}>Cycles</p>
                    <p className="text-2xl font-black" style={{ fontFamily: '"Russo One", sans-serif', color: INK }}>{active.cycles}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT — 3D device on reflective studio floor */}
            <div className="lg:col-span-7 relative" data-testid="terraform-3d">
              <div className="relative w-full h-[440px] md:h-[520px] flex items-center justify-center">
                {/* reflective floor */}
                <div className="absolute left-1/2 -translate-x-1/2 bottom-12 pointer-events-none" style={{ width: '80%', height: '180px', background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.12) 0%, rgba(255,134,24,0.10) 38%, transparent 70%)', borderRadius: '50%' }} />
                <div className="absolute left-1/2 -translate-x-1/2 bottom-16 pointer-events-none" style={{ width: '64%', height: '120px', border: '1px solid rgba(255,255,255,0.14)', borderRadius: '50%' }} />
                <div className="absolute left-1/2 -translate-x-1/2 bottom-20 pointer-events-none" style={{ width: '44%', height: '80px', border: '1px solid rgba(255,134,24,0.16)', borderRadius: '50%' }} />
                <iframe
                  src="/model-viewer.html?src=/models/terraform_device_compressed.glb"
                  title="Terraform Device"
                  style={{ width: '100%', height: '100%', border: 'none', background: 'transparent', position: 'relative', zIndex: 2 }}
                  data-testid="terraform-model-iframe"
                />
              </div>

              {/* Phase buttons */}
              <div className="flex justify-center gap-4 -mt-4" data-testid="terraform-thumbs">
                {TERRAFORM_PHASES.map((t) => {
                  const isActive = activePhase === t.id;
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setActivePhase(t.id)}
                      data-testid={`terraform-thumb-${t.id}`}
                      className="group flex flex-col items-center justify-center gap-2 px-7 py-4 rounded-xl transition-all duration-300"
                      style={{
                        minWidth: '170px',
                        background: isActive ? 'linear-gradient(180deg, #FF8618 0%, #F07310 100%)' : 'rgba(20,27,34,0.75)',
                        border: isActive ? '1px solid #C6530D' : '1px solid rgba(255,255,255,0.12)',
                        boxShadow: isActive ? '0 12px 30px rgba(255,134,24,0.30)' : '0 8px 20px rgba(0,0,0,0.4)',
                        transform: isActive ? 'translateY(-2px)' : 'translateY(0)',
                      }}
                    >
                      <Icon className="w-6 h-6" style={{ color: isActive ? '#131A20' : RED }} />
                      <span className="text-xs font-bold uppercase tracking-[0.18em]" style={{ fontFamily: '"Rajdhani", sans-serif', color: isActive ? '#131A20' : 'var(--text-muted)' }}>{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TerraformDevice;
